/* =========================================================================
   VÉLTHERA — scripts propios
   Un init por componente, todos llamados desde DOMContentLoaded.
   Solo transform/opacity, un único IntersectionObserver compartido para
   reveals, rAF compartido para parallax. Respeta prefers-reduced-motion.
   ========================================================================= */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Reveals al hacer scroll ---------------- */
  function initReveals() {
    var els = document.querySelectorAll('.mt-reveal, .mt-reveal-scale, .mt-reveal-blur, .mt-reveal-mask');
    if (!els.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('mt-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('mt-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    /* Red de seguridad: si por lo que sea el navegador nunca dispara el
       observer (extensiones, pestaña en segundo plano al cargar, etc.),
       no dejamos contenido invisible para siempre. */
    window.setTimeout(function () {
      els.forEach(function (el) {
        if (!el.classList.contains('mt-visible')) {
          el.classList.add('mt-visible');
          observer.unobserve(el);
        }
      });
    }, 2500);

    els.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------- Parallax suave ---------------- */
  function initParallax() {
    var els = Array.prototype.slice.call(document.querySelectorAll('.mt-parallax-el'));
    if (!els.length || reducedMotion) return;

    var active = new Set();
    var ticking = false;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            active.add(entry.target);
          } else {
            active.delete(entry.target);
            entry.target.style.transform = '';
          }
        });
        if (active.size && !ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { rootMargin: '20% 0px 20% 0px' }
    );

    els.forEach(function (el) {
      io.observe(el);
    });

    function update() {
      var vh = window.innerHeight;
      active.forEach(function (el) {
        var speed = parseFloat(el.dataset.speed || '0.15');
        var rect = el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var offset = (vh / 2 - center) * speed;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      if (active.size) {
        requestAnimationFrame(update);
      } else {
        ticking = false;
      }
    }

    window.addEventListener(
      'scroll',
      function () {
        if (active.size && !ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
  }

  /* ---------------- Mide el aviso superior para que el menú no se solape ---------------- */
  function initAnnouncementOffset() {
    if (!document.body.classList.contains('mt-has-overlay-hero')) return;
    var announcement = document.querySelector('.shopify-section-group-header-group [class*="announcement-bar"]');
    if (!announcement) {
      announcement = document.querySelector('.shopify-section-group-header-group')?.firstElementChild;
    }

    function measure() {
      var height = announcement ? announcement.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--mt-announce-height', height + 'px');
    }

    measure();
    window.addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
  }

  /* ---------------- Header inteligente ---------------- */
  function initHeader() {
    var header = document.querySelector('.mt-site-header');
    if (!header) return;

    var threshold = 64;
    var ticking = false;

    function apply() {
      var scrolled = window.scrollY > threshold;
      header.classList.toggle('is-scrolled', scrolled);
      ticking = false;
    }

    apply();
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(apply);
        }
      },
      { passive: true }
    );
  }

  /* ---------------- Galería de producto ---------------- */
  function initGallery() {
    var galleries = document.querySelectorAll('.mt-gallery');
    galleries.forEach(function (gallery) {
      var main = gallery.querySelector('.mt-gallery__main img');
      var thumbs = gallery.querySelectorAll('.mt-gallery__thumb');
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.dataset.full;
          var alt = thumb.dataset.alt || '';
          if (!full) return;
          main.style.opacity = '0';
          window.setTimeout(function () {
            main.src = full;
            main.alt = alt;
            main.style.opacity = '1';
          }, 160);
          thumbs.forEach(function (t) {
            t.classList.remove('is-active');
          });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------------- Barra inferior sticky (móvil) ---------------- */
  function initMobileBuyBar() {
    var bar = document.querySelector('.mt-mobile-buybar');
    var trigger = document.querySelector('[data-mt-buybar-trigger]');
    if (!bar || !trigger) return;

    var barButton = bar.querySelector('[data-mt-buybar-submit]');
    var realForm = trigger.closest('form');

    if (barButton && realForm) {
      barButton.addEventListener('click', function () {
        var realSubmit = realForm.querySelector('[type="submit"]');
        if (realSubmit) realSubmit.click();
      });
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('mt-hide', entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );
    io.observe(trigger);
  }

  /* ---------------- Sincroniza el selector de variantes con precio/compra ---------------- */
  function initVariantSync() {
    document.querySelectorAll('[data-mt-variant-sync]').forEach(function (wrapper) {
      var script = wrapper.querySelector('script[data-mt-variants]');
      var variantSelects = wrapper.querySelector('variant-selects');
      if (!script || !variantSelects) return;

      var variants;
      try {
        variants = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }

      var form = wrapper.parentElement.querySelector('form[data-type="add-to-cart-form"]');
      var idInput = form ? form.querySelector('input[name="id"]') : null;
      var priceTargetId = wrapper.dataset.priceTarget;
      var priceEl = priceTargetId ? document.getElementById(priceTargetId) : null;
      var submitBtn = form ? form.querySelector('[type="submit"]') : null;

      function currentSelection() {
        var values = [];
        variantSelects.querySelectorAll('select').forEach(function (sel) {
          values.push(sel.value);
        });
        if (values.length === 0) {
          variantSelects.querySelectorAll('fieldset').forEach(function (fs) {
            var checked = fs.querySelector('input:checked');
            if (checked) values.push(checked.value);
          });
        }
        return values;
      }

      function findVariant(values) {
        return variants.filter(function (v) {
          return (
            v.options.length === values.length &&
            v.options.every(function (o, i) {
              return o === values[i];
            })
          );
        })[0];
      }

      function formatMoney(amount) {
        return (amount / 100).toFixed(2).replace('.', ',') + ' €';
      }

      function applyVariant(variant) {
        if (!variant) return;

        if (idInput) idInput.value = variant.id;

        if (priceEl) {
          priceEl.innerHTML = '<span class="price price--large"><span class="price-item price-item--regular">' +
            formatMoney(parseFloat(variant.price)) +
            '</span></span>';
        }

        if (submitBtn) {
          var span = submitBtn.querySelector('span');
          if (variant.available) {
            submitBtn.removeAttribute('disabled');
            submitBtn.removeAttribute('aria-disabled');
            if (span) span.textContent = (window.variantStrings && window.variantStrings.addToCart) || 'Añadir al carrito';
          } else {
            submitBtn.setAttribute('disabled', 'disabled');
            submitBtn.setAttribute('aria-disabled', 'true');
            if (span) span.textContent = (window.variantStrings && window.variantStrings.soldOut) || 'Agotado';
          }
        }
      }

      variantSelects.addEventListener('change', function () {
        applyVariant(findVariant(currentSelection()));
      });

      applyVariant(findVariant(currentSelection()));
    });
  }

  /* ---------------- Contadores animados ---------------- */
  function initCountUp() {
    var els = document.querySelectorAll('[data-mt-count-to]');
    if (!els.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          var to = parseFloat(el.dataset.mtCountTo);
          var suffix = el.dataset.mtCountSuffix || '';
          if (reducedMotion || isNaN(to)) {
            el.textContent = to + suffix;
            return;
          }
          var start = null;
          var duration = 1400;
          function step(ts) {
            if (!start) start = ts;
            var progress = Math.min((ts - start) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            var value = to * eased;
            el.textContent = (Number.isInteger(to) ? Math.round(value) : value.toFixed(1)) + suffix;
            if (progress < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.6 }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------- Marquesina de confianza (loop CSS, sin JS extra) --- */

  document.addEventListener('DOMContentLoaded', function () {
    initAnnouncementOffset();
    initReveals();
    initParallax();
    initHeader();
    initGallery();
    initMobileBuyBar();
    initCountUp();
    initVariantSync();
  });
})();
